import type { PartialId } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'

import type * as models from '../models'

import type * as expression from './expression'
import type * as query from './query'
import type { Record } from './record'

/**
 * Inserts (or upserts) records for a given model
 */
export interface Insertion {
  modelId: models.ForRecord.Id
  inputs: Insertion.Inputs
  /**
   * @default Insertion.OnConflict.Overwrite
   * @see {@link Insertion.OnConflict}
   */
  onConflict?: Insertion.OnConflict
}
export namespace Insertion {
  export type Inputs = ExactlyOneOf<{
    records: readonly Record.Input[]
    query: query.ForList<Record.Input>
  }>

  export type WithDefaults = Required<Insertion>
  export namespace WithDefaults {
    export function from({
      modelId,
      inputs,
      onConflict = OnConflict.Overwrite,
    }: Insertion): WithDefaults {
      return {
        inputs,
        modelId,
        onConflict,
      }
    }
  }

  /**
   * Determines what is done when inserting a record that already exists
   */
  export enum OnConflict {
    /**
     * When inserting a record that already exists, an error is thrown
     */
    Error = 'error',
    /**
     * When inserting a record that already exists, the existing record is
     * overwritten by the insertion
     */
    Overwrite = 'overwrite',
    /**
     * When inserting a record that already exists, no changes are made
     */
    Skip = 'skip',
  }
  // Needs to be before namespace is defined
  const ON_CONFLICTS: ReadonlySet<OnConflict> = new Set(
    Object.values(OnConflict) as OnConflict[],
  )
  export namespace OnConflict {
    export const ALL: readonly OnConflict[] = Array.from(ON_CONFLICTS)
    export const isOnConflict = TypeChecker.Factory.oneOf(ON_CONFLICTS)
    export const assertIsOnConflict: TypeAsserter<OnConflict> =
      TypeAsserter.Factory.from(isOnConflict)
  }
}

/**
 * Updates records of a given model based on a given expression
 */
export interface Update {
  modelId: models.ForRecord.Id
  /**
   * Defines how to update the records of this model. This can either be an
   * object specifying how to update certain attributes of every record of the
   * given model, or it can be inputs for insertion, which will be used to
   * overwrite attributes of certain existing records.
   */
  update: Update.Updates
  /**
   * Determines which records to update
   */
  condition?: expression.fn.Fn<boolean>
}
export namespace Update {
  export type Updates = ExactlyOneOf<
    Required<FlattenUnion<Insertion.Inputs>> & {
      attributes: globalThis.Record<Record.Values.Key, expression.Expression>
    }
  >
}

/**
 * Deletes specific records of a given model
 */
export interface Deletion {
  modelId: models.ForRecord.Id
  // TODO: should we allow deleting based on a condition?
  ids: readonly PartialId[]
}

/**
 * Deletes __ALL__ records of a given model
 */
export interface Truncation {
  modelId: models.ForRecord.Id
}

/**
 * A change to the data
 */
export type Mutation = ExactlyOneOf<{
  insertion: Insertion
  update: Update
  deletion: Deletion
  truncation: Truncation
}>
